<script lang="ts">
  import { storyblokEditable } from '@storyblok/svelte';
  import { InnerPageSection, PageSection } from '@athena-lib/Page';
  import type { CollectionItemBlock, CollectionPageSectionBlock } from '@storyblok-types';
  import { Grid, GridItem } from '@athena-lib/Grid';
  import clsx from 'clsx';
  import { FullwidthCarousel, FullwidthCarouselItem } from '@athena-lib/Carousel';
  import { Stack, StackItem } from '@athena-lib/Stack';
  import { containerAlignClasses, type Alignment } from '../shared/page-section-utils';
  import PageSectionHeading from '../shared/PageSectionHeading.svelte';
  import { isRichTextEmpty } from '../../RichText/types';
  import CollectionMedia from './CollectionMedia.svelte';

  const { blok }: { blok: CollectionPageSectionBlock } = $props();
  const getItemProps = (collectionItem: CollectionItemBlock) => {
    const baseProps = {
      mobileItemTextAlignment: blok.mobileItemTextAlignment as Alignment | undefined,
      desktopItemTextAlignment: blok.desktopItemTextAlignment as Alignment | undefined,
      imageBoxColor: blok.imageBoxColor,
      padding: collectionItem.padding,
      zoomOnHover: blok.zoomOnHover ?? false,
      fit: blok.mediaFit === 'contain' ? ('contain' as const) : ('cover' as const),
    };
    const specificItem = collectionItem.specificItem;
    if (typeof specificItem === 'object') {
      return {
        ...baseProps,
        description: specificItem.content.imageCardDescription,
        asset: specificItem.content.imageCardImage,
      };
    }
    return {
      ...baseProps,
      description: collectionItem.description,
      asset: collectionItem.image,
    };
  };
  const items = $derived((blok.items ?? []).map((x) => getItemProps(x)).filter(Boolean));

  const hasHeadingOrDescription = $derived(
    !isRichTextEmpty(blok.description) || blok.headingText?.trim(),
  );
</script>

<PageSection
  id={blok.elementId}
  action={storyblokEditable}
  actionArg={blok}
  ySpace={{ top: blok.topPadding ?? 'medium', bottom: blok.bottomPadding ?? 'medium' }}
  xSpace="none"
  backgroundColor={blok.backgroundColor ?? 'none'}
  width="full"
>
  <Stack space="xlarge">
    {#if hasHeadingOrDescription || !blok.desktopEnableSwipe || !blok.mobileEnableSwipe}
      <StackItem>
        <InnerPageSection>
          <Stack space="xlarge">
            {#if hasHeadingOrDescription}
              <StackItem>
                <div
                  class={clsx(
                    'flex',
                    containerAlignClasses['desktop'][
                      (blok.desktopTextAlignment as Alignment) ?? 'left'
                    ],
                    containerAlignClasses['mobile'][
                      (blok.mobileTextAlignment as Alignment) ?? 'left'
                    ],
                  )}
                >
                  <div class="max-w-text-max">
                    <PageSectionHeading
                      headingText={blok.headingText}
                      headingTag={blok.headingTag}
                      headingSize={blok.headingSize}
                      description={blok.description}
                      mobileAlignment={blok.mobileTextAlignment}
                      desktopAlignment={blok.desktopTextAlignment}
                    />
                  </div>
                </div>
              </StackItem>
            {/if}
            {#if !blok.desktopEnableSwipe || !blok.mobileEnableSwipe}
              <div
                class={clsx(
                  blok.mobileEnableSwipe ? 'hidden' : 'block',
                  blok.desktopEnableSwipe ? 'md:hidden' : 'md:block',
                )}
              >
                <StackItem>
                  <Grid columns={{ lg: 4, md: 2, sm: 1 }} space="xlarge">
                    {#each items as item}
                      <GridItem>
                        <CollectionMedia {...item} />
                      </GridItem>
                    {/each}
                  </Grid>
                </StackItem>
              </div>
            {/if}
          </Stack>
        </InnerPageSection>
      </StackItem>
    {/if}

    {#if blok.desktopEnableSwipe || blok.mobileEnableSwipe}
      <StackItem
        class={clsx(
          blok.mobileEnableSwipe ? 'block' : 'hidden',
          blok.desktopEnableSwipe ? 'md:block' : 'md:hidden',
        )}
      >
        <FullwidthCarousel space={{ sm: 'medium', md: 'xlarge' }}>
          {#each items as item}
            <FullwidthCarouselItem>
              <div class="max-w-64">
                <CollectionMedia {...item} />
              </div>
            </FullwidthCarouselItem>
          {/each}
        </FullwidthCarousel>
      </StackItem>
    {/if}
  </Stack>
</PageSection>
